import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';
import { 
  Building2, 
  Calendar, 
  Clock, 
  ArrowLeft,
  Leaf,
  Users,
  Heart,
  Shield,
  CheckCircle,
  Target,
  TrendingUp,
  Award,
  FileText,
  Lightbulb
} from 'lucide-react';

export default function RSEMarchesPublicsArticle() {
  return (
    <>
      <SEO 
        title="RSE et Marchés Publics : Guide Complet pour les Entreprises 2026"
        description="Comment intégrer la RSE dans vos réponses aux appels d'offres publics ? Critères sociaux, environnementaux et de gouvernance. Guide pratique 2026."
        path="/blog/rse-marches-publics-guide"
      />
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
          <div className="container mx-auto px-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-dce-express.png" alt="DCE Express" className="h-10 w-auto" />
            </Link>
            <div className="flex gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Article */}
        <article className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            {/* Breadcrumb */}
            <Link to="/blog" className="inline-flex items-center gap-2 text-violet-600 hover:text-violet-700 mb-8">
              <ArrowLeft className="w-4 h-4" />
              Retour au blog
            </Link>

            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                  RSE
                </span>
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                  Guide Pratique
                </span>
              </div>
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                RSE et Marchés Publics : Comment Valoriser Votre Engagement Responsable
              </h1>
              <div className="flex items-center gap-6 text-slate-500">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  30 mars 2026
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  12 min de lecture
                </span>
              </div>
            </div>

            {/* Featured Image */}
            <div className="relative h-80 rounded-2xl overflow-hidden mb-10">
              <img 
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200" 
                alt="RSE et développement durable en entreprise"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-white text-lg font-medium">
                  La RSE devient un critère déterminant dans l'attribution des marchés publics
                </p>
              </div>
            </div>

            {/* Table of Contents */}
            <div className="bg-slate-50 rounded-xl p-6 mb-10">
              <h2 className="font-bold text-lg mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-violet-600" />
                Sommaire
              </h2>
              <ul className="space-y-2 text-slate-600">
                <li><a href="#introduction" className="hover:text-violet-600">1. Introduction : La RSE dans la commande publique</a></li>
                <li><a href="#piliers" className="hover:text-violet-600">2. Les 3 piliers de la RSE</a></li>
                <li><a href="#criteres" className="hover:text-violet-600">3. Critères RSE dans les marchés publics</a></li>
                <li><a href="#valoriser" className="hover:text-violet-600">4. Comment valoriser votre démarche RSE</a></li>
                <li><a href="#labels" className="hover:text-violet-600">5. Labels et certifications RSE</a></li>
                <li><a href="#exemples" className="hover:text-violet-600">6. Exemples de clauses RSE</a></li>
                <li><a href="#conseils" className="hover:text-violet-600">7. Conseils pratiques pour PME</a></li>
              </ul>
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              {/* Introduction */}
              <section id="introduction" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                  <Leaf className="w-6 h-6 text-green-600" />
                  1. La RSE dans la Commande Publique
                </h2>
                <p className="text-slate-600 mb-4">
                  La <strong>Responsabilité Sociétale des Entreprises (RSE)</strong> est devenue un enjeu majeur dans les marchés publics français. Depuis la loi Climat et Résilience de 2021 et le Plan National pour des Achats Durables (PNAD), les acheteurs publics intègrent de plus en plus de critères RSE dans leurs consultations.
                </p>
                <p className="text-slate-600 mb-4">
                  En 2026, près de <strong>70% des marchés publics</strong> incluent au moins un critère social ou environnemental. Pour les entreprises, c'est une opportunité de se différencier et de remporter des marchés en valorisant leur engagement responsable.
                </p>
                
                <div className="bg-violet-50 border-l-4 border-violet-500 p-6 rounded-r-lg my-6">
                  <p className="text-violet-900 font-medium">
                    <strong>Chiffre clé :</strong> Les entreprises avec une démarche RSE structurée ont un taux de réussite aux appels d'offres supérieur de 25% par rapport à leurs concurrents.
                  </p>
                </div>
              </section>

              {/* Les 3 piliers */}
              <section id="piliers" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Target className="w-6 h-6 text-blue-600" />
                  2. Les 3 Piliers de la RSE
                </h2>
                
                <div className="grid md:grid-cols-3 gap-6 mb-6">
                  <div className="bg-green-50 rounded-xl p-6">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <Leaf className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">Environnemental</h3>
                    <ul className="text-slate-600 text-sm space-y-1">
                      <li>- Réduction des émissions CO2</li>
                      <li>- Gestion des déchets</li>
                      <li>- Économie circulaire</li>
                      <li>- Biodiversité</li>
                      <li>- Éco-conception</li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 rounded-xl p-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">Social</h3>
                    <ul className="text-slate-600 text-sm space-y-1">
                      <li>- Conditions de travail</li>
                      <li>- Égalité homme-femme</li>
                      <li>- Insertion professionnelle</li>
                      <li>- Formation</li>
                      <li>- Handicap</li>
                    </ul>
                  </div>
                  
                  <div className="bg-purple-50 rounded-xl p-6">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                      <Shield className="w-6 h-6 text-purple-600" />
                    </div>
                    <h3 className="font-bold text-slate-900 mb-2">Gouvernance</h3>
                    <ul className="text-slate-600 text-sm space-y-1">
                      <li>- Éthique des affaires</li>
                      <li>- Transparence</li>
                      <li>- Lutte anti-corruption</li>
                      <li>- Protection des données</li>
                      <li>- Relations fournisseurs</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Critères RSE dans les marchés */}
              <section id="criteres" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  3. Critères RSE dans les Marchés Publics
                </h2>
                
                <p className="text-slate-600 mb-6">
                  Les acheteurs publics peuvent intégrer la RSE à plusieurs niveaux du processus d'achat :
                </p>

                <div className="space-y-4 mb-6">
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <h4 className="font-bold text-slate-900 mb-2">Conditions d'exécution (Article L2112-2)</h4>
                    <p className="text-slate-600 text-sm">
                      Clauses obligatoires à respecter pendant l'exécution du marché : insertion de personnes éloignées de l'emploi, utilisation de véhicules propres, recyclage des déchets...
                    </p>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <h4 className="font-bold text-slate-900 mb-2">Critères d'attribution (Article L2152-7)</h4>
                    <p className="text-slate-600 text-sm">
                      Critères notés lors de l'analyse des offres : performance environnementale, politique sociale, bilan carbone, approvisionnement local...
                    </p>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-5">
                    <h4 className="font-bold text-slate-900 mb-2">Spécifications techniques</h4>
                    <p className="text-slate-600 text-sm">
                      Exigences techniques liées à la RSE : labels écologiques, matériaux recyclés, efficacité énergétique, durabilité des produits...
                    </p>
                  </div>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
                  <p className="text-amber-900">
                    <strong>Important :</strong> Depuis août 2026, tout marché public doit obligatoirement inclure au moins un critère environnemental dans ses conditions d'attribution.
                  </p>
                </div>
              </section>

              {/* Valoriser sa démarche */}
              <section id="valoriser" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <TrendingUp className="w-6 h-6 text-violet-600" />
                  4. Comment Valoriser Votre Démarche RSE
                </h2>
                
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-violet-600">1</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">Formalisez votre politique RSE</h4>
                      <p className="text-slate-600">Rédigez une charte RSE claire avec des objectifs mesurables et un plan d'action concret.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-violet-600">2</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">Mesurez vos impacts</h4>
                      <p className="text-slate-600">Réalisez votre bilan carbone, suivez vos indicateurs sociaux, documentez vos actions avec des données chiffrées.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-violet-600">3</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">Obtenez des certifications</h4>
                      <p className="text-slate-600">Les labels et certifications apportent une crédibilité et simplifient la preuve de vos engagements.</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-violet-600">4</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">Adaptez votre mémoire technique</h4>
                      <p className="text-slate-600">Créez une section RSE dans vos réponses aux appels d'offres avec des preuves concrètes et des engagements spécifiques.</p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Labels et certifications */}
              <section id="labels" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Award className="w-6 h-6 text-amber-600" />
                  5. Labels et Certifications RSE Reconnus
                </h2>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-bold text-slate-900 mb-2">Labels Environnementaux</h4>
                    <ul className="text-slate-600 text-sm space-y-1">
                      <li>- ISO 14001 (Management environnemental)</li>
                      <li>- Écolabel Européen</li>
                      <li>- NF Environnement</li>
                      <li>- PEFC / FSC (Bois durable)</li>
                      <li>- Cradle to Cradle</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-bold text-slate-900 mb-2">Labels RSE Globaux</h4>
                    <ul className="text-slate-600 text-sm space-y-1">
                      <li>- Label Lucie 26000</li>
                      <li>- B Corp</li>
                      <li>- Engagé RSE (AFNOR)</li>
                      <li>- EcoVadis</li>
                      <li>- Global Compact</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-bold text-slate-900 mb-2">Labels Sociaux</h4>
                    <ul className="text-slate-600 text-sm space-y-1">
                      <li>- ISO 45001 (Santé et sécurité)</li>
                      <li>- Label Diversité</li>
                      <li>- Label Égalité</li>
                      <li>- Entreprise Inclusive</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h4 className="font-bold text-slate-900 mb-2">Labels Sectoriels</h4>
                    <ul className="text-slate-600 text-sm space-y-1">
                      <li>- HQE (Bâtiment)</li>
                      <li>- BREEAM / LEED</li>
                      <li>- Imprim'Vert (Imprimerie)</li>
                      <li>- QualiPV / RGE (Énergie)</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Exemples de clauses */}
              <section id="exemples" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <FileText className="w-6 h-6 text-slate-600" />
                  6. Exemples de Clauses RSE dans les Marchés
                </h2>
                
                <div className="bg-slate-50 rounded-xl p-6 mb-4">
                  <h4 className="font-bold text-slate-900 mb-3">Clause d'insertion sociale</h4>
                  <p className="text-slate-600 text-sm italic">
                    "Le titulaire s'engage à réserver au moins 5% des heures travaillées à des personnes en insertion professionnelle (bénéficiaires du RSA, demandeurs d'emploi longue durée, travailleurs handicapés)."
                  </p>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-6 mb-4">
                  <h4 className="font-bold text-slate-900 mb-3">Clause environnementale</h4>
                  <p className="text-slate-600 text-sm italic">
                    "Le candidat devra présenter son bilan carbone et ses actions de réduction des émissions de GES. Les livraisons devront être effectuées avec des véhicules à faibles émissions (Crit'Air 1 ou électrique)."
                  </p>
                </div>
                
                <div className="bg-slate-50 rounded-xl p-6">
                  <h4 className="font-bold text-slate-900 mb-3">Critère de notation RSE</h4>
                  <p className="text-slate-600 text-sm italic">
                    "La performance environnementale et sociale sera notée sur 20 points, soit 20% de la note finale. Seront évalués : la politique RSE (5 pts), les certifications (5 pts), les engagements spécifiques au marché (10 pts)."
                  </p>
                </div>
              </section>

              {/* Conseils PME */}
              <section id="conseils" className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                  <Lightbulb className="w-6 h-6 text-amber-500" />
                  7. Conseils Pratiques pour les PME
                </h2>
                
                <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl p-6">
                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <span className="text-slate-700"><strong>Commencez petit</strong> : Identifiez 2-3 actions RSE faciles à mettre en place et à documenter.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <span className="text-slate-700"><strong>Impliquez vos équipes</strong> : La RSE est plus crédible quand elle est portée collectivement.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <span className="text-slate-700"><strong>Documentez tout</strong> : Gardez des preuves de vos actions (photos, attestations, factures...).</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <span className="text-slate-700"><strong>Valorisez le local</strong> : L'approvisionnement local et les circuits courts sont très appréciés.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      <span className="text-slate-700"><strong>Anticipez les critères</strong> : Lisez attentivement le RC pour identifier les critères RSE et y répondre précisément.</span>
                    </li>
                  </ul>
                </div>
              </section>

              {/* CTA */}
              <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-8 text-center text-white">
                <h3 className="text-2xl font-bold mb-4">
                  Trouvez des marchés publics avec critères RSE
                </h3>
                <p className="text-violet-100 mb-6 max-w-xl mx-auto">
                  DCE Express identifie automatiquement les appels d'offres incluant des critères RSE adaptés à votre secteur.
                </p>
                <Link to="/register">
                  <Button className="bg-white text-violet-600 hover:bg-slate-100 px-8 py-3 text-lg">
                    Essayer gratuitement
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-12">
          <div className="container mx-auto px-4 text-center">
            <p className="text-slate-400">
              © 2026 DCE Express - Tous droits réservés
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
